import React, { useEffect, useState } from "react";
import clsx from "clsx";

import CommonCheckbox from "../../components/Input/Checkbox";
import HorizontalRule from "../../components/Rule/Horizontal";
import SimplePopup from "../../components/Popup/Simple";

export type AgreementItem = {
  title: string;
  required: boolean;
  onClick?: () => boolean;
  disabled?: boolean;
};

type Props = {
  agreementList: AgreementItem[];
  allTitle: string;
  onAllClick?: () => boolean;
};

export default function useAgreement({
  agreementList,
  allTitle,
  onAllClick,
}: Props): [
  {
    isComplete: boolean;
    agreements: boolean[];
    validate: () => boolean;
    reset: () => void;
  },
  React.ReactNode
] {
  const [agreement, setAgreement] = useState<boolean[]>(
    agreementList.map((item) => false)
  );
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const requiredList = agreementList
    .filter((item) => item.required)
    .map((item, index) => index);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [popup, setPopup] = useState<string>("");

  const validate = () => {
    if (
      agreement
        .filter((item, index) => requiredList.includes(index))
        .every((agreement) => agreement) === false
    ) {
      setErrorMessage("필수 약관에 동의해주세요.");
      setPopup("agreementError");
      return false;
    }

    setErrorMessage("");
    return true;
  };

  const reset = () => {
    setAgreement(agreementList.map((item) => false));
  };

  useEffect(() => {
    if (
      agreement
        .filter((item, index) => requiredList.includes(index))
        .every((agreement) => agreement)
    ) {
      setIsComplete(true);
    } else {
      setIsComplete(false);
    }
  }, [agreement]);

  return [
    { isComplete, agreements: agreement, validate, reset },
    <div>
      <div className="flex flex-col">
        <div className="pb-1.5">
          <div
            className="px-2.5 flex gap-2.5 items-center h-[60px]"
            onClick={() => {
              if (agreement.every((item) => item)) {
                setAgreement((prev) => prev.map((item) => false));
              } else {
                const result = onAllClick?.();
                if (result === false) return;

                setAgreement((prev) => prev.map((item) => true));
              }
            }}
          >
            <CommonCheckbox
              checked={agreement
                // #883 이슈에서 선택 항목도 필수로 체크하도록 했다.
                // .filter((item, index) => requiredList.includes(index))
                .every((item) => item)}
            />
            <div>{allTitle}</div>
          </div>
          <HorizontalRule margin="0" className="bg-black" />
        </div>
        <div className="pb-1.5">
          {agreementList.map((agreementItem, agreementItemIndex) => (
            <div
              className={clsx([
                "px-2.5 flex gap-2.5 items-center h-[60px] justify-between",
                {
                  "opacity-60": agreementItem?.disabled,
                },
              ])}
              key={agreementItemIndex}
              onClick={() => {
                if (agreement[agreementItemIndex] === false) {
                  const result = agreementItem.onClick?.();
                  if (result === false) return;
                }

                setAgreement((prev) =>
                  prev.map((item, index) =>
                    index === agreementItemIndex ? !item : item
                  )
                );
              }}
            >
              <div className="flex items-center gap-2.5">
                <CommonCheckbox checked={agreement[agreementItemIndex]} />
                <div>
                  {agreementItem.title}
                  {agreementItem.required ? (
                    <span className="text-brand-primary-red">(필수)</span>
                  ) : (
                    <span>(선택)</span>
                  )}
                </div>
              </div>
              {agreementItem.onClick && (
                <div className=" justify-self-end">
                  <AgreementRightArrow />
                </div>
              )}
            </div>
          ))}
        </div>
        <HorizontalRule margin="0" />
      </div>

      <SimplePopup
        message={errorMessage}
        isOpen={popup === "agreementError"}
        onClose={() => setPopup("")}
      />
    </div>,
  ];
}

export const AgreementRightArrow = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 18.9854L15 12.9854L9 6.98535"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);
